import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { MembershipStatus, PersonAgeStatus } from '../generated/api-client';

export interface MemberFollowUpItem {
  id: string;
  fullName: string;
  communityName: string;
  status: MembershipStatus;
  ageStatus?: PersonAgeStatus | null;
  age?: number | null;
  responsiblePriestId?: string | null;
  responsiblePriestName?: string | null;
  isHomeAttended: boolean;
  lastAttendanceAt?: string | null;
  lastSpiritualAssistanceVisitDate?: string | null;
  lastHolyCommunionDate?: string | null;
}

export interface MemberFollowUpResponse {
  items: MemberFollowUpItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface MemberFollowUpRequest {
  page: number;
  pageSize: number;
  searchTerm?: string;
  ageStatuses?: PersonAgeStatus[];
  noAgeStatusesSelected?: boolean;
  includeUndeterminedAge?: boolean;
  responsiblePriestIds?: string[];
  noResponsiblePriestsSelected?: boolean;
  isHomeAttended?: boolean | null;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface MemberFollowUpResponsiblePriest {
  id: string;
  fullName: string;
  ministryName: string;
  ministryActivityUnitNames: string[];
}

export interface MemberFollowUpResponsiblePriestPage {
  items: MemberFollowUpResponsiblePriest[];
  totalCount: number;
  pageNumber: number;
  hasNextPage: boolean;
}

@Injectable({ providedIn: 'root' })
export class MemberFollowUpApiService {
  private readonly url = `${environment.apiUrl}/people/follow-up`;

  constructor(private readonly http: HttpClient) {}

  get(unitId: string, request: MemberFollowUpRequest): Observable<MemberFollowUpResponse> {
    let params = new HttpParams()
      .set('unitId', unitId)
      .set('page', request.page)
      .set('pageSize', request.pageSize);

    if (request.searchTerm?.trim()) params = params.set('searchTerm', request.searchTerm.trim());
    if (request.noAgeStatusesSelected) params = params.set('noAgeStatusesSelected', true);
    if (request.includeUndeterminedAge) params = params.set('includeUndeterminedAge', true);
    for (const responsiblePriestId of request.responsiblePriestIds ?? []) params = params.append('responsiblePriestIds', responsiblePriestId);
    if (request.noResponsiblePriestsSelected) params = params.set('noResponsiblePriestsSelected', true);
    if (request.isHomeAttended !== null && request.isHomeAttended !== undefined) params = params.set('isHomeAttended', request.isHomeAttended);
    if (request.sortBy) params = params.set('sortBy', request.sortBy);
    if (request.sortDirection) params = params.set('sortDirection', request.sortDirection);
    for (const ageStatus of request.ageStatuses ?? []) params = params.append('ageStatuses', ageStatus);

    return this.http.get<MemberFollowUpResponse>(this.url, { params });
  }

  getResponsiblePriests(unitId: string, searchTerm = '', page = 1): Observable<MemberFollowUpResponsiblePriestPage> {
    return this.http.get<MemberFollowUpResponsiblePriestPage>(`${this.url}/responsible-priests`, {
      params: new HttpParams().set('unitId', unitId).set('searchTerm', searchTerm).set('page', page).set('pageSize', 20),
    });
  }
}
